import React, { useState, useEffect } from 'react';
import { categoriesApi, signalMappingsApi } from '../services/api';
import {
  Settings,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  Search,
  Filter,
  TrendingUp,
  Loader,
  Info
} from 'lucide-react';

interface SignalMapping {
  id: number;
  category_id: number;
  category_name: string;
  domain_name: string | null;
  signal_name: string;
  min_count: number;
  ideal_count: number | null;
  weight: number;
  is_required: boolean;
  description: string | null;
}

interface Category {
  id: number;
  name: string;
  domain_name: string;
}

interface SignalMappingsProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const SignalMappings: React.FC<SignalMappingsProps> = ({ addNotification }) => {
  const [mappings, setMappings] = useState<SignalMapping[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState<SignalMapping | null>(null);
  const [statistics, setStatistics] = useState<any>(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSignal, setFilterSignal] = useState<string>('all');
  const [filterRequired, setFilterRequired] = useState<string>('all');

  // Form state
  const [formData, setFormData] = useState({
    category_id: 0,
    signal_name: 'sentences',
    min_count: 10,
    ideal_count: null as number | null,
    weight: 1.0,
    is_required: false,
    description: ''
  });

  const signalTypes = [
    { name: 'sections', icon: '📄', description: 'Document structure sections' },
    { name: 'sentences', icon: '📝', description: 'Content sentence count' },
    { name: 'entities', icon: '🏷️', description: 'Named entities' },
    { name: 'tables', icon: '📊', description: 'Structured data tables' },
    { name: 'references', icon: '🔗', description: 'Cross-references' },
    { name: 'topics', icon: '🎯', description: 'Key themes' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [mappingsData, categoriesData, statsData] = await Promise.all([
        signalMappingsApi.list(),
        categoriesApi.list({ page: 1, size: 100 }),
        signalMappingsApi.statisticsOverview(),
      ]);

      const mappingsList = Array.isArray(mappingsData)
        ? mappingsData
        : (mappingsData as any)?.mappings ?? (mappingsData as any)?.results ?? (mappingsData as any)?.items ?? [];
      setMappings(mappingsList);
      setCategories(Array.isArray(categoriesData?.categories) ? categoriesData.categories.map((c: any) => ({
        id: c.id,
        name: c.name,
        domain_name: c.domain_name || 'Unknown'
      })) : []);
      setStatistics(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
      addNotification('Failed to load signal mappings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (formData.category_id === 0) {
      addNotification('Please select a category', 'error');
      return;
    }

    try {
      await signalMappingsApi.create(formData);

      addNotification('✅ Signal mapping created successfully', 'success');
      setShowAddModal(false);
      resetForm();
      loadData();
    } catch (error: any) {
      addNotification(error.message || 'Failed to create mapping', 'error');
    }
  };

  const handleUpdate = async () => {
    if (!editingMapping) return;

    try {
      await signalMappingsApi.update(editingMapping.id, {
        min_count: formData.min_count,
        ideal_count: formData.ideal_count,
        weight: formData.weight,
        is_required: formData.is_required,
        description: formData.description,
      });

      addNotification('✅ Signal mapping updated successfully', 'success');
      setEditingMapping(null);
      resetForm();
      loadData();
    } catch (error) {
      addNotification('Failed to update mapping', 'error');
    }
  };

  const handleDelete = async (mapping: SignalMapping) => {
    if (!window.confirm(`Delete mapping: ${mapping.category_name} → ${mapping.signal_name}?`)) {
      return;
    }

    try {
      await signalMappingsApi.delete(mapping.id);

      addNotification('✅ Signal mapping deleted successfully', 'success');
      loadData();
    } catch (error) {
      addNotification('Failed to delete mapping', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: 0,
      signal_name: 'sentences',
      min_count: 10,
      ideal_count: null,
      weight: 1.0,
      is_required: false,
      description: ''
    });
  };

  const openEditModal = (mapping: SignalMapping) => {
    setEditingMapping(mapping);
    setFormData({
      category_id: mapping.category_id,
      signal_name: mapping.signal_name,
      min_count: mapping.min_count,
      ideal_count: mapping.ideal_count,
      weight: mapping.weight,
      is_required: mapping.is_required,
      description: mapping.description || ''
    });
  };

  // Filter mappings (guard: API may return object instead of array)
  const mappingsArray = Array.isArray(mappings) ? mappings : [];
  const filteredMappings = mappingsArray.filter(m => {
    const matchesSearch = searchTerm === '' || 
      m.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.signal_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSignal = filterSignal === 'all' || m.signal_name === filterSignal;
    const matchesRequired = filterRequired === 'all' || 
      (filterRequired === 'required' && m.is_required) ||
      (filterRequired === 'optional' && !m.is_required);
    
    return matchesSearch && matchesSignal && matchesRequired;
  });

  // Group mappings by category
  const groupedMappings = filteredMappings.reduce((groups, mapping) => {
    const key = mapping.category_name;
    if (!groups[key]) groups[key] = [];
    groups[key].push(mapping);
    return groups;
  }, {} as Record<string, SignalMapping[]>);

  const getSignalIcon = (signalName: string) => {
    const signal = signalTypes.find(s => s.name === signalName);
    return signal?.icon || '📌';
  };

  const getConfidenceColor = (weight: number) => {
    if (weight >= 1.5) return 'bg-red-100 text-red-800';
    if (weight >= 1.0) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100 flex items-center">
              <Settings className="h-7 w-7 mr-3 text-indigo-600" />
              Signal Mappings
            </h1>
            <p className="text-slate-300 mt-2">
              Configure which content signals trigger which test category recommendations
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Mapping
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-indigo-900">{statistics.total_mappings}</div>
            <div className="text-sm text-slate-300">Total Mappings</div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-900">{statistics.categories_with_mappings}</div>
            <div className="text-sm text-slate-300">Categories Configured</div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-900">{statistics.required_mappings}</div>
            <div className="text-sm text-slate-300">Required Signals</div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-900">{statistics.optional_mappings}</div>
            <div className="text-sm text-slate-300">Optional Signals</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories or signals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </div>

          <select
            value={filterSignal}
            onChange={(e) => setFilterSignal(e.target.value)}
            className="px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
          >
            <option value="all">All Signals</option>
            {signalTypes.map(s => (
              <option key={s.name} value={s.name}>{s.icon} {s.name}</option>
            ))}
          </select>

          <select
            value={filterRequired}
            onChange={(e) => setFilterRequired(e.target.value)}
            className="px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
          >
            <option value="all">All Types</option>
            <option value="required">Required Only</option>
            <option value="optional">Optional Only</option>
          </select>
        </div>

        <div className="mt-2 text-sm text-slate-300">
          Showing {filteredMappings.length} of {mappingsArray.length} mappings
        </div>
      </div>

      {/* Mappings List */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Signal Requirements by Category</h2>
        
        <div className="space-y-4">
          {Object.entries(groupedMappings).map(([categoryName, categoryMappings]) => (
            <div key={categoryName} className="border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-medium text-slate-100">{categoryName}</h3>
                  <p className="text-sm text-slate-400">{categoryMappings[0].domain_name || 'No Domain'}</p>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                  {categoryMappings.length} signal{categoryMappings.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="space-y-2">
                {categoryMappings.map((mapping) => (
                  <div
                    key={mapping.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getSignalIcon(mapping.signal_name)}</span>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-slate-100">{mapping.signal_name}</span>
                            {mapping.is_required && (
                              <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">
                                REQUIRED
                              </span>
                            )}
                            <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(mapping.weight)}`}>
                              Weight: {mapping.weight.toFixed(1)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-300 mt-1">
                            Min: {mapping.min_count}
                            {mapping.ideal_count && ` | Ideal: ${mapping.ideal_count}`}
                            {mapping.description && ` | ${mapping.description}`}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openEditModal(mapping)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(mapping)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {filteredMappings.length === 0 && (
            <div className="text-center py-12">
              <Filter className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-slate-400">No mappings found matching your filters</p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingMapping) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-slate-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-slate-100">
                {editingMapping ? 'Edit Signal Mapping' : 'Add Signal Mapping'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingMapping(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-slate-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Test Category *
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: parseInt(e.target.value) })}
                  disabled={!!editingMapping}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100"
                >
                  <option value={0}>Select a category...</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} ({cat.domain_name})
                    </option>
                  ))}
                </select>
              </div>

              {/* Signal Type */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Signal Type *
                </label>
                <select
                  value={formData.signal_name}
                  onChange={(e) => setFormData({ ...formData, signal_name: e.target.value })}
                  disabled={!!editingMapping}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 disabled:bg-gray-100"
                >
                  {signalTypes.map(s => (
                    <option key={s.name} value={s.name}>
                      {s.icon} {s.name} - {s.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Min Count */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Minimum Count *
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.min_count}
                  onChange={(e) => setFormData({ ...formData, min_count: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                  placeholder="e.g., 10"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Minimum signal count required to recommend this category
                </p>
              </div>

              {/* Ideal Count */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Ideal Count (Optional)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.ideal_count || ''}
                  onChange={(e) => setFormData({ ...formData, ideal_count: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                  placeholder="e.g., 50 (leave empty for no limit)"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Optimal count for best match scoring (leave empty for no upper limit)
                </p>
              </div>

              {/* Weight */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Weight (Importance) *
                </label>
                <input
                  type="number"
                  min="0.1"
                  max="2.0"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) || 1.0 })}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-400">
                    0.5 = Low importance | 1.0 = Normal | 2.0 = Critical
                  </p>
                  <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(formData.weight)}`}>
                    {formData.weight >= 1.5 ? 'Critical' : formData.weight >= 1.0 ? 'Important' : 'Nice to have'}
                  </span>
                </div>
              </div>

              {/* Is Required */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.is_required}
                  onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 focus:ring-purple-500 border-slate-600 rounded mt-1"
                />
                <div className="ml-3">
                  <label className="text-sm font-medium text-slate-200">
                    Required Signal
                  </label>
                  <p className="text-xs text-slate-400">
                    If checked, category will ONLY be recommended if this signal meets minimum count
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                  placeholder="Explain why this signal is important for this category..."
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingMapping(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={editingMapping ? handleUpdate : handleAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {editingMapping ? 'Update' : 'Create'} Mapping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">How Signal Mappings Work</h4>
            <p className="text-xs text-blue-700">
              When a document is analyzed, it extracts signals (sections, entities, etc.). The recommendation engine then matches 
              these signals against the requirements you configure here. Categories with matching signals are automatically 
              recommended during test case generation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignalMappings;





